import { Component, OnInit, Input, Output, EventEmitter, SimpleChanges, OnChanges } from '@angular/core';
import { CustomInputTypesDefinitionsService } from '../../../services/configuration/custom-input-types-definitions.service';

@Component({
  selector: 'app-custom-input-type',
  templateUrl: './custom-input-type.component.html',
  styleUrls: ['./custom-input-type.component.scss']
})
export class CustomInputTypeComponent implements OnInit, OnChanges {
  @Input() inputTypeConfiguration: any;
  @Input() showDefaultType: boolean;
  @Input() canEdit: boolean;
  @Input() classes: any;
  @Input() formElementId: string;
  @Input() inputTypeDefinitions: any;
  @Output() updateValue = new EventEmitter<any>();

  DEFAULT_INPUT_TYPE = 0;
  value: any = null;
  initComplete = false;
  typeOfInput: number;
  options: any[];
  minLength: number;
  maxLength: number;
  placeholderText: string;
  ariaLabelText: string;

  constructor(private customInputTypesDefinitionsService: CustomInputTypesDefinitionsService) {}

  ngOnInit(): void {
    if (!this.inputTypeDefinitions) {
      this.customInputTypesDefinitionsService.getDefinitions().subscribe(
        values => {
          this.inputTypeDefinitions = values;
          this.initialisationSetUp();
        },
        error => console.log(error)
      );
    } else {
      this.initialisationSetUp();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['inputTypeConfiguration']) {
      this.configureTypeAndInitialValue();
    }
  }

  initialisationSetUp(): void {
    this.setDefaultInputType();
    this.initComplete = true;
    this.configureTypeAndInitialValue();
  }

  setDefaultInputType(): void {
    this.DEFAULT_INPUT_TYPE = this.inputTypeDefinitions.FREE_TEXT;
  }

  convertToNumber(valueToConvert: any): number | null {
    if (this.valueIsIndeterminate(valueToConvert)) {
      return null;
    }

    if (typeof valueToConvert === 'number' && Number.isFinite(valueToConvert)) {
      return valueToConvert;
    }

    // Convert the value to a number
    const convertedValue = Number(valueToConvert);
    const convertedValueString = convertedValue.toString();

    if (Number.isFinite(convertedValue) && valueToConvert === convertedValueString) {
      return convertedValue;
    }

    console.error('Error converting value to number:', valueToConvert);
    return null;
  }

  valueIsIndeterminate(value: any): boolean {
    return value === null || value === undefined;
  }

  convertTo2DecimalNumber(valueToConvert: any): string | null {
    const convertedNumber = this.convertToNumber(valueToConvert);

    if (convertedNumber !== null) {
      return convertedNumber.toFixed(2);
    } else {
      return null;
    }
  }

  convertToCheckboxValue(valueToConvert: any): boolean | null {
    switch (valueToConvert) {
      case "true":
      case true:
        return true;
      case "false":
      case false:
        return false;
      case null:
      case undefined:
        return null;
      default:
        console.error('Error converting value to checkbox value:', valueToConvert);
        return null;
    }
  }

  convertToDate(valueToConvert: any): Date | null {
    if (this.valueIsIndeterminate(valueToConvert)) {
      return null;
    }

    if (valueToConvert instanceof Date) {
      return valueToConvert;
    }

    const convertedValue = new Date(valueToConvert);
    if (!isNaN(convertedValue.valueOf())) {
      const isoString = convertedValue.toISOString();
      if (isoString === valueToConvert) {
        return convertedValue;
      }
    }

    console.error('Error converting value to date:', valueToConvert);
    return null;
  }

  getInitialInputType(): any {
    return Object.values(this.inputTypeDefinitions).find(t => t === this.inputTypeConfiguration.typeId);
  }

  setPlaceholderText(): void {
    this.placeholderText = 'Value'; // Replace with localization as needed

    if (this.inputTypeConfiguration) {
      if (!this.inputTypeConfiguration.includePlaceholderText) {
        this.placeholderText = '';
      } else {
        if (this.inputTypeConfiguration.placeholderText) {
          this.placeholderText = this.inputTypeConfiguration.placeholderText;
        }
      }
    }
  }

  setAriaLabelText(): void {
    this.ariaLabelText = 'Value'; // Replace with localization as needed

    if (this.inputTypeConfiguration && this.inputTypeConfiguration.labelText) {
      this.ariaLabelText = this.inputTypeConfiguration.labelText;
    }
  }

  configureTypeAndInitialValue(): void {
    this.setPlaceholderText();
    this.setAriaLabelText();

    if (!this.inputTypeConfiguration || !this.inputTypeConfiguration.typeId) {
      if (this.showDefaultType) {
        this.typeOfInput = this.DEFAULT_INPUT_TYPE;
        this.value = null;
      }
    } else {
      if (this.initComplete) {
        if (this.getInitialInputType()) {
          this.typeOfInput = this.inputTypeConfiguration.typeId;

          switch (this.typeOfInput) {
            case this.inputTypeDefinitions.NUMERIC_INT:
            case this.inputTypeDefinitions.NUMERIC_DEC:
            case this.inputTypeDefinitions.YEAR:
              this.value = this.convertToNumber(this.inputTypeConfiguration.initialValue);
              break;
            case this.inputTypeDefinitions.CURRENCY:
              this.value = this.convertTo2DecimalNumber(this.inputTypeConfiguration.initialValue);
              break;
            case this.inputTypeDefinitions.DROP_DOWN:
              this.options = this.inputTypeConfiguration.options;
              this.value = this.inputTypeConfiguration.initialValue;
              break;
            case this.inputTypeDefinitions.CHECKBOX:
              this.value = this.convertToCheckboxValue(this.inputTypeConfiguration.initialValue);
              break;
            case this.inputTypeDefinitions.DATE:
              this.value = this.convertToDate(this.inputTypeConfiguration.initialValue);
              break;
            default:
              this.value = this.inputTypeConfiguration.initialValue;
              break;
          }

          this.minLength = this.inputTypeConfiguration.minLength || 0;
          this.maxLength = this.inputTypeConfiguration.maxLength || 50;
        } else {
          console.error('Error getting initial input type');
        }
      }
    }
  }

  isDefinedTypeOfInput(): boolean {
    return this.typeOfInput !== undefined && this.typeOfInput !== 0;
  }

  isFreeText(): boolean {
    return this.isDefinedTypeOfInput() && this.typeOfInput === this.inputTypeDefinitions.FREE_TEXT;
  }

  isParagraph(): boolean {
    return this.isDefinedTypeOfInput() && this.typeOfInput === this.inputTypeDefinitions.PARAGRAPH;
  }

  isNumericInt(): boolean {
    return this.isDefinedTypeOfInput() && this.typeOfInput === this.inputTypeDefinitions.NUMERIC_INT;
  }

  isNumericDec(): boolean {
    return this.isDefinedTypeOfInput() && this.typeOfInput === this.inputTypeDefinitions.NUMERIC_DEC;
  }

  isCurrency(): boolean {
    return this.isDefinedTypeOfInput() && this.typeOfInput === this.inputTypeDefinitions.CURRENCY;
  }

  isDropDown(): boolean {
    return this.isDefinedTypeOfInput() && this.typeOfInput === this.inputTypeDefinitions.DROP_DOWN;
  }

  isCheckbox(): boolean {
    return this.isDefinedTypeOfInput() && this.typeOfInput === this.inputTypeDefinitions.CHECKBOX;
  }

  isDate(): boolean {
    return this.isDefinedTypeOfInput() && this.typeOfInput === this.inputTypeDefinitions.DATE;
  }

  isYear(): boolean {
    return this.isDefinedTypeOfInput() && this.typeOfInput === this.inputTypeDefinitions.YEAR;
  }

  setValueUpdated(): void {
    this.updateValue.emit(this.value);
  }

  canSelectFromDropdown(): boolean {
    if (!this.canEdit) {
      return false;
    }

    return this.isDropDown() && this.options && this.options.length > 0;
  }

  valueIsRequired(valueToCheck: any): boolean {
    let required = true;
    if (this.inputTypeConfiguration) {
      required = this.inputTypeConfiguration.required;
      if (required === undefined) {
        required = false;
      }

      if (this.isCheckbox()) {
        return this.valueIsIndeterminate(valueToCheck) && required;
      }
    }

    return required;
  }
}
